import React from 'react';
import { Helmet } from 'react-helmet-async';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy-page">
      <Helmet>
        <title>Paycis UK – Privacy Policy | Data Protection &amp; GDPR</title>
        <meta name="description" content="Read Paycis UK's Privacy Policy to understand how we collect, use, and protect your personal data. Learn about your rights, GDPR compliance, and our commitment to data security." />
        <meta property="og:title" content="Paycis UK – Privacy Policy | Data Protection & GDPR" />
      </Helmet>
      <div className="privacy-policy-container">
        <h1>PAYCIS – PRIVACY & DATA USAGE NOTICE</h1>
        <p className="last-updated">Last updated: 01/15/2026</p>
        <p className="controller"><strong>Controller:</strong> PayCIS</p>
        <p className="controller"><strong>Contact:</strong> <a style={{ color: '#fff' }} href="mailto:privacy@paycis.co.uk">privacy@paycis.co.uk</a></p>
        
        <div className="privacy-content">
          <section>
            <h2>1. Introduction</h2>
            <p>
              PayCIS ("we", "us", "our") provides a secure online platform enabling UK construction contractors (employers) and subcontractors to manage payments and compliance under the Construction Industry Scheme (CIS).
            </p>
            <p>
              This notice explains how we collect, use, store, share and protect personal data in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
            </p>
            <p>
              For the purposes of data protection law, PayCIS acts as a data controller in relation to the operation of the platform and the facilitation of data sharing between users.
            </p>
          </section>

          <section>
            <h2>2. Who this notice applies to</h2>
            <p>This notice applies to:</p>
            <ul>
              <li>Subcontractors who register and use PayCIS</li>
              <li>Contractors / employers who use PayCIS to verify and pay subcontractors</li>
              <li>Authorised users acting on behalf of contractors (e.g. administrators, bookkeepers)</li>
              <li>Visitors to the PayCIS website</li>
            </ul>
          </section>

          <section>
            <h2>3. Information we collect</h2>
            <p>
              We collect and process only data required for lawful operation of the PayCIS platform, including:
            </p>
            
            <h3>Identity & contact data</h3>
            <ul>
              <li>Name and title</li>
              <li>Business or trading name</li>
              <li>Address, email address, telephone number</li>
              <li>Company registration number (where applicable)</li>
            </ul>

            <h3>Tax & employment data</h3>
            <ul>
              <li>CIS registration status</li>
              <li>Unique Taxpayer Reference (UTR)</li>
              <li>National Insurance number (NI)</li>
              <li>Verification codes and deduction rate</li>
            </ul>

            <h3>Financial data</h3>
            <ul>
              <li>Bank name, account number and sort code</li>
              <li>VAT registration number (if applicable)</li>
              <li>Materials costs, payment amounts and payment history</li>
            </ul>

            <h3>Account & technical data</h3>
            <ul>
              <li>Email address and encrypted password</li>
              <li>Login session data and IP logs</li>
              <li>System activity and audit logs</li>
            </ul>

            <p>
              PayCIS does not store or process debit or credit card numbers. Payment services are provided by third-party providers such as GoCardless and Stripe.
            </p>
          </section>

          <section>
            <h2>4. How we use personal data and our lawful basis</h2>
            <p>
              Under UK GDPR, we must identify a lawful basis for each processing activity. PayCIS relies on the following:
            </p>
            <div className="data-usage-table">
              <table>
                <thead>
                  <tr>
                    <th>Purpose</th>
                    <th>Lawful basis</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>User registration and account management</td>
                    <td>Contract – Article 6(1)(b)</td>
                  </tr>
                  <tr>
                    <td>Connecting contractors and subcontractors</td>
                    <td>Contract – Article 6(1)(b) / Legitimate interests – Article 6(1)(f)</td>
                  </tr>
                  <tr>
                    <td>CIS verification and monthly returns to HMRC</td>
                    <td>Legal obligation – Article 6(1)(c)</td>
                  </tr>
                  <tr>
                    <td>Processing payments and issuing payslips</td>
                    <td>Contract – Article 6(1)(b)</td>
                  </tr>
                  <tr>
                    <td>System notifications and user support</td>
                    <td>Legitimate interests – Article 6(1)(f)</td>
                  </tr>
                  <tr>
                    <td>Fraud prevention, audit and security</td>
                    <td>Legitimate interests – Article 6(1)(f)</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>
              Processing is limited to what is necessary to achieve these purposes.
            </p>
          </section>

          <section>
            <h2>5. Connections between users and data sharing</h2>
            <p>
              When a subcontractor accepts a connection or invitation from a contractor through PayCIS, relevant personal data from the subcontractor's profile — including identity, contact, tax and payment information — is shared with that contractor.
            </p>
            <p>This sharing is necessary to:</p>
            <ul>
              <li>verify CIS status,</li>
              <li>process payments, and</li>
              <li>enable contractors to meet their legal obligations to HMRC.</li>
            </ul>
            <p>
              Subcontractors are clearly informed of this data sharing at the point the connection is made and must actively accept and acknowledge the connection before it becomes active. PayCIS records the date, time and contractor name associated with this acceptance for audit purposes.
            </p>
            <p>
              Once data is shared, the contractor acts as an independent data controller in respect of that data and is responsible for its lawful use and protection.
            </p>
            <p>
              Subcontractors can view and remove active connections at any time via their account dashboard.
            </p>
          </section>

          <section>
            <h2>6. Legitimate interests explanation</h2>
            <p>
              Where PayCIS relies on legitimate interests, these interests include enabling contractors to pay subcontractors, maintain lawful records, prevent fraud, and comply with CIS and HMRC requirements. This processing is expected by users of the platform and is limited to connected parties, with appropriate access controls in place to minimise any privacy impact.
            </p>
            <p>
              Individuals have the right to object to processing based on legitimate interests; however, objection may mean PayCIS is unable to continue providing certain services (such as payment processing).
            </p>
          </section>

          <section>
            <h2>7. Data sharing with third parties</h2>
            <p>We may share personal data with:</p>
            <ul>
              <li><strong>HM Revenue & Customs (HMRC)</strong> for CIS verification and monthly returns</li>
              <li><strong>Payment providers</strong> such as GoCardless, Stripe and banking partners</li>
              <li><strong>Accounting integrations</strong> (e.g. Xero) where enabled by users</li>
              <li><strong>Hosting, security and IT service providers</strong> operating under confidentiality obligations</li>
              <li><strong>Regulators or law enforcement bodies</strong> where required by law</li>
            </ul>
            <p>
              We do not sell personal data.
            </p>
          </section>

          <section>
            <h2>8. Data retention</h2>
            <p>We retain personal data only for as long as necessary, including:</p>
            <ul>
              <li><strong>Tax and payment records:</strong> 6 years from the end of the relevant tax year</li>
              <li><strong>User account data:</strong> deleted or anonymised 12 months after account closure</li>
              <li><strong>Connection and audit logs:</strong> retained while required for compliance and security purposes</li>
            </ul>
          </section>

          <section>
            <h2>9. Security measures</h2>
            <p>PayCIS implements appropriate technical and organisational measures to protect personal data, including:</p>
            <ul>
              <li>Encrypted connections (TLS)</li>
              <li>Secure UK-based hosting</li>
              <li>Role-based access controls</li>
              <li>Activity logging and audit trails</li>
              <li>No use of live personal data in development environments</li>
            </ul>
          </section>

          <section>
            <h2>10. Your rights</h2>
            <p>Under UK GDPR, individuals have the right to:</p>
            <ul>
              <li>Access their personal data</li>
              <li>Request correction of inaccurate data</li>
              <li>Request erasure where legally permitted</li>
              <li>Restrict or object to processing</li>
              <li>Request data portability (where applicable)</li>
            </ul>
            <p>
              Requests can be made by contacting <a href="mailto:privacy@paycis.co.uk">privacy@paycis.co.uk</a>.
            </p>
            <p>
              If you are dissatisfied with how your data is handled, you have the right to complain to the Information Commissioner's Office (ICO) at <a href="https://www.ico.org.uk" target="_blank" rel="noopener noreferrer">www.ico.org.uk</a>.
            </p>
          </section>

          <section>
            <h2>11. Cookies and analytics</h2>
            <p>
              PayCIS uses essential cookies required for login, security and platform functionality. Analytics cookies are used only where consent is provided via the cookie banner.
            </p>
          </section>

          <section>
            <h2>12. Changes to this notice</h2>
            <p>
              We may update this notice from time to time. The latest version will always be available on our website, and material changes will be communicated where appropriate.
            </p>
          </section>

          <section>
            <h2>13. Acceptance</h2>
            <p>
              By registering for or continuing to use PayCIS, you confirm that you have read and understood this Privacy & Data Usage Notice and acknowledge the processing of your personal data as described above.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;

